import { useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import { analyticsClient } from '../utils/analytics';
import { hideUntil, isHiddenUntil, track } from '../utils/appAdoptionStorage';

interface AdoptionPromptInput {
  hiddenKey: string;
  actHideMs: number;
  dismissHideMs: number;
  platform: string;
  role: string;
  serviceDeskName: string;
}

/** The app adoption prompts' shared tap logic: the first tap tracks and hides, a later one does nothing. */
export const useAdoptionPrompt = ({ hiddenKey, actHideMs, dismissHideMs, platform, role, serviceDeskName }: AdoptionPromptInput) => {
  const [dismissed, setDismissed] = useState(() => isHiddenUntil(hiddenKey));
  const actedRef = useRef(false);
  const props = { platform, role, client: analyticsClient(serviceDeskName) };

  const trackPrompt = (eventName: string): void => track(eventName, props);

  const act = (eventName: string) => (event: MouseEvent<HTMLAnchorElement>): void => {
    if (actedRef.current) {
      event.preventDefault();
      return;
    }
    actedRef.current = true;
    trackPrompt(eventName);
    hideUntil(hiddenKey, actHideMs);
    // Deferred so the anchor is still mounted when the browser follows it
    setTimeout(() => setDismissed(true), 0);
  };

  const dismiss = (eventName: string) => (): void => {
    // Already counted and persisted by the tap, whose deferred hide has not run yet
    if (!actedRef.current) {
      trackPrompt(eventName);
      hideUntil(hiddenKey, dismissHideMs);
    }
    setDismissed(true);
  };

  return { dismissed, trackPrompt, act, dismiss };
};
